import type { Block, ContentAST, HeadingBlock, InlineContent, ListItem } from './astTypes'
import { getBlockText } from './astUtils'
import { stripChapterSectionHeadingPrefix } from './tocChapterSectionHeading'
import { analyzeWritingSyntax } from './writingSyntax'

/**
 * Locate a `^`-marked section in `ast.blocks` and return its bounds.
 * The section runs from the heading through its explicit ^^ delimiter.
 */
function locateChapterSectionRange(
  ast: ContentAST,
  headingBlockId: string,
): { startIdx: number; endIdx: number; heading: HeadingBlock; rawTitle: string } | null {
  const section = analyzeWritingSyntax(ast.blocks).sections.find((s) => s.heading.id === headingBlockId)
  if (!section) {
    return null
  }
  return {
    startIdx: section.start,
    endIdx: section.end,
    heading: section.heading,
    rawTitle: getBlockText(section.heading),
  }
}

/**
 * Extract one "section" from the clean area: from the given `^`-marked heading until
 * its explicit ^^ delimiter. An unfinished section ends at the next section or EOF; readers hide it.
 */
export function extractSectionFromChapterSectionHeading(
  ast: ContentAST,
  headingBlockId: string,
): { slice: ContentAST; sectionTitle: string } | null {
  const range = locateChapterSectionRange(ast, headingBlockId)
  if (!range) {
    return null
  }
  const { startIdx, endIdx, rawTitle } = range
  const sectionBlocks = ast.blocks.slice(startIdx, endIdx)
  const sectionTitle = stripChapterSectionHeadingPrefix(rawTitle).trim() || rawTitle
  const slice: ContentAST = {
    ...ast,
    meta: { ...ast.meta },
    blocks: sectionBlocks,
  }
  return { slice, sectionTitle }
}

/**
 * Replace the section identified by `headingBlockId` in `source` with `editedBlocks`.
 * Used when an edit happened on a section slice (e.g. the OrganizeChapters preview editor)
 * and we need to fold the edits back into the full source file before saving.
 *
 * Returns a new ContentAST. Returns null if the heading can't be located in `source`
 * (e.g. it was deleted by a concurrent edit).
 */
export function spliceChapterSectionIntoSource(
  source: ContentAST,
  headingBlockId: string,
  editedBlocks: Block[],
): ContentAST | null {
  const range = locateChapterSectionRange(source, headingBlockId)
  if (!range) {
    return null
  }
  const { startIdx, endIdx } = range
  const nextBlocks = [...source.blocks.slice(0, startIdx), ...editedBlocks, ...source.blocks.slice(endIdx)]
  return {
    ...source,
    meta: { ...source.meta },
    blocks: nextBlocks,
  }
}

/**
 * Walk backwards from `blockId` (inclusive) in `ast.blocks` to find the nearest
 * preceding heading marked with `^`. Returns its blockId, or null if none precedes.
 * Use to determine which ChapterSection a given block (e.g. the cursor's block) is inside.
 */
export function findEnclosingChapterSectionHeadingBlockId(ast: ContentAST, blockId: string): string | null {
  const blocks = ast.blocks
  const idx = blocks.findIndex((b) => b.id === blockId)
  if (idx === -1) {
    return null
  }
  return analyzeWritingSyntax(blocks).sections.find((s) => s.start <= idx && idx < s.end)?.heading.id ?? null
}

function walkInlineEncryptedIds(inline: InlineContent[], out: Set<string>): void {
  for (const n of inline) {
    if (n.type === 'encrypted') {
      out.add(n.id)
    }
  }
}

function walkListItems(items: ListItem[], out: Set<string>): void {
  for (const item of items) {
    walkInlineEncryptedIds(item.content, out)
    if (item.children?.length) {
      walkListItems(item.children, out)
    }
  }
}

/** Section ids for encrypted inline nodes present in this AST. */
export function collectEncryptedSectionIdsFromAst(ast: ContentAST): Set<string> {
  const out = new Set<string>()
  for (const block of ast.blocks) {
    if ('content' in block && Array.isArray(block.content)) {
      walkInlineEncryptedIds(block.content as InlineContent[], out)
    }
    if (block.type === 'list') {
      walkListItems(block.items, out)
    }
    if (block.type === 'image' && block.caption?.length) {
      walkInlineEncryptedIds(block.caption, out)
    }
  }
  return out
}
